import React, { useEffect, useState, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RadixMultiSelect } from '@/components/radix';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { useProductService } from '@/hooks/api/pos/products/useProducts';
import { usePlaygroundFormRegistry } from '../../hooks/usePlaygroundFormRegistry';
import { DURATION_OPERATORS_WITHIN_AFTER } from '@/data/Marketing/marketingDurationOperators';
import DaySelector, { type DaySelectorLabel } from './DaySelector';
import {
    buildCompositeFromDaySelectorValue,
    DURATION_HOURS_TOUCHED,
    getDaySelectorValueFromComposite,
} from './durationPeriodUtils';

type ProductMultiSelectValue = string[] | string | number[];

interface ProductMultiSelectCompositeValues {
    productIds?: string[] | string;
    days?: [number | null, number | null];
    selectedDurationFormat?: DaySelectorLabel;
}

interface ProductMultiSelectProps {
    value: ProductMultiSelectValue | null | undefined;
    compositeValues?: ProductMultiSelectCompositeValues;
    conditionInstanceId: string;
    hasPeriod?: boolean; // Explicitly control whether to show days input
}

const ProductMultiSelect = ({
    value,
    compositeValues,
    conditionInstanceId,
    hasPeriod: explicitHasPeriod,
}: ProductMultiSelectProps) => {
    const { register } = usePlaygroundFormRegistry();
    const hasPeriod = explicitHasPeriod !== undefined ? explicitHasPeriod : !!compositeValues;
    interface SettingsState {
        settings?: {
            data?: {
                profile?: {
                    outlet_addons?: Array<{ addon_name?: string }>;
                };
            };
        };
    }
    const settings = useSelector((state: SettingsState) => state?.settings?.data);
    const [isPOSEnabled, setIsPOSEnabled] = useState(false);

    // Check if POS is enabled
    useEffect(() => {
        const outletAddons = settings?.profile?.outlet_addons || [];
        const hasPOS = outletAddons.some((addon) => addon.addon_name === 'POS');
        setIsPOSEnabled(hasPOS);
    }, [settings]);

    // Memoize query params to prevent infinite re-renders
    const queryParams = useMemo(() => ({ page: 1, limit: 1000 }), []);

    // Fetch products if POS is enabled
    const { data: productsData, isLoading } = useProductService(queryParams, 500, 0, isPOSEnabled, isPOSEnabled);

    // Flatten products from categories
    const productOptions: Array<{ label: string; value: string }> = [];
    if (productsData?.products) {
        productsData.products.forEach((category) => {
            if (category.products && Array.isArray(category.products)) {
                category.products.forEach((product: { id: number | string; name: string; isActive?: boolean }) => {
                    // Only include active products
                    if (product.isActive !== false) {
                        productOptions.push({
                            label: product.name,
                            value: String(product.id),
                        });
                    }
                });
            }
        });
    }

    // Extract values from compositeValues or use defaults
    const productIds = hasPeriod ? compositeValues?.productIds || [] : value;
    const initialDuration = getDaySelectorValueFromComposite(compositeValues, {
        includeOperator: hasPeriod,
        defaultOperator: 'AFTER_HOURS',
    });

    const formik = useFormik({
        initialValues: {
            productIds: hasPeriod
                ? Array.isArray(productIds)
                    ? productIds.map(String)
                    : typeof productIds === 'string'
                      ? productIds
                      : ''
                : Array.isArray(productIds)
                  ? productIds.map(String)
                  : productIds
                    ? [String(productIds)]
                    : [],
            duration: initialDuration,
        },
        validationSchema: Yup.object().shape({
            productIds: hasPeriod
                ? Yup.mixed().test('required', t('Marketing.ProductsRequired'), (value) => {
                      if (Array.isArray(value)) {
                          return value.length > 0;
                      }
                      return typeof value === 'string' && value.trim().length > 0;
                  })
                : Yup.array().of(Yup.string()).min(1, t('Marketing.ProductsRequired')),
            duration: hasPeriod
                ? Yup.object().shape({
                      hours: Yup.number().test(
                          'required',
                          t('Marketing.DaysRequired'),
                          (val) => val !== null && val !== undefined,
                      ),
                  })
                : Yup.mixed().notRequired(),
        }),
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: () => {
            // Validation handled automatically
        },
    });

    useEffect(() => {
        register(conditionInstanceId, {
            validate: async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                    formik.setTouched({
                        productIds: true,
                        ...(hasPeriod ? { duration: DURATION_HOURS_TOUCHED } : {}),
                    });
                    return false;
                }
                return true;
            },
            getValues: () => {
                if (hasPeriod) {
                    return {
                        productIds: Array.isArray(formik.values.productIds)
                            ? formik.values.productIds
                            : typeof formik.values.productIds === 'string'
                              ? formik.values.productIds
                              : [],
                        ...buildCompositeFromDaySelectorValue(formik.values.duration),
                    };
                }
                return formik.values.productIds;
            },
        });
        // Keep validator in registry on unmount so we can validate all at once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionInstanceId, formik.values, hasPeriod, register]);

    const handleProductChange = (selectedValues: Set<string>) => {
        const productIdsArray = Array.from(selectedValues);
        formik.setFieldValue('productIds', productIdsArray);
    };

    const selectedValuesSet = new Set(
        Array.isArray(formik.values.productIds)
            ? formik.values.productIds
            : typeof formik.values.productIds === 'string'
              ? []
              : formik.values.productIds
                ? [String(formik.values.productIds)]
                : [],
    );

    return (
        <div className={hasPeriod ? 'flex flex-col gap-4' : 'w-full'}>
            {/* Product Selection */}
            <div className="w-full">
                {!isPOSEnabled ? (
                    <p className="text-sm leading-5 font-normal text-text-primary m-0 p-0 mb-1">
                        <span className="text-error-500">⚠️{t('Marketing.POSNotEnabled')}</span>
                    </p>
                ) : (
                    <>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                            {t('Marketing.SelectProducts')}
                        </label>
                        <RadixMultiSelect
                            options={productOptions}
                            selectedValues={selectedValuesSet}
                            onSelectionChange={handleProductChange}
                            placeholder={t('Marketing.SelectProducts')}
                            textToDisplayWithCount={t('POS.ProdSelect')}
                            className="w-full min-w-full"
                            disabled={isLoading}
                        />
                    </>
                )}
                {formik.touched.productIds && formik.errors.productIds && (
                    <div className="text-sm text-red-500 mt-1">
                        {typeof formik.errors.productIds === 'string'
                            ? formik.errors.productIds
                            : Array.isArray(formik.errors.productIds)
                              ? (formik.errors.productIds as string[]).join(', ')
                              : t('Marketing.ProductsRequired')}
                    </div>
                )}
            </div>

            {/* Days Range Input - only show if period mode */}
            {hasPeriod && (
                <DaySelector
                    label={t('Marketing.DaysRange')}
                    showOperator
                    operatorOptions={DURATION_OPERATORS_WITHIN_AFTER}
                    value={formik.values.duration}
                    onChange={(next) => formik.setFieldValue('duration', next)}
                    summaryNoteKey="Marketing.DaySelectorNoteProductPurchase"
                    summaryParams={{
                        itemSelection:
                            productOptions
                                .filter((p) =>
                                    (Array.isArray(formik.values.productIds)
                                        ? formik.values.productIds
                                        : []
                                    ).includes(p.value),
                                )
                                .map((p) => p.label)
                                .join(', ') || t('Marketing.DaySelectorSelectedItems'),
                    }}
                    showError={Boolean(
                        formik.touched.duration &&
                            (formik.errors.duration as { hours?: string } | undefined)?.hours,
                    )}
                    error={
                        typeof (formik.errors.duration as { hours?: string } | undefined)?.hours === 'string'
                            ? (formik.errors.duration as { hours?: string }).hours
                            : t('Marketing.DaysRequired')
                    }
                />
            )}
        </div>
    );
};

export default ProductMultiSelect;
